/**
 * Settings → Dashboard Designer.
 *
 * The client-facing designer that lets a lab admin customise the backlog
 * dashboards WITHOUT a software release — add / reorder / rename / resize columns
 * from a curated field catalog and choose which KPI cards show, per Tenant +
 * Division.  This replaces ALPACA's DevExpress XAF Dashboards designer (which we
 * cannot port — DevExpress is a paid library, ADR-0005) with a license-clean
 * equivalent built entirely on the shared components (AppDataGrid / AppToolbar /
 * FormTitle / useSaveFlow).
 *
 * PHASE: UI-first.  The field catalog + saved layout live in dashboardCatalog.ts
 * and currently persist to localStorage; a later phase swaps loadDefinition /
 * saveDefinition for the config API (GET/PUT /api/v1/dashboard/definition) and
 * the wide backlog SPs, with no change to this component.  The layout it produces
 * is what SampleBacklogPage will consume instead of its hardcoded COLS.
 */
import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import {
  DASH_VIEWS, FIELD_CATALOG,
  defaultDefinition, loadDefinition, saveDefinition,
  type DashViewId, type DashDefinition, type DashColumn, type FieldFormat,
} from './dashboardCatalog';
import './backlog.css';
import './dashboardDesigner.css';

const FORMATS: FieldFormat[] = ['text', 'number', 'date', 'datetime', 'pill'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PreviewRow = Record<string, any>;

/** Build a handful of sample rows so the preview shows realistic cells. */
function sampleRows(def: DashDefinition): PreviewRow[] {
  const now = new Date();
  const iso = (dayOffset: number) => new Date(now.getTime() + dayOffset * 86_400_000).toISOString();
  return Array.from({ length: 4 }, (_, i) => {
    const row: PreviewRow = {};
    for (const c of def.columns) {
      switch (c.format) {
        case 'number':   row[c.key] = (i + 1) * 7; break;
        case 'date':     row[c.key] = iso(i - 1); break;
        case 'datetime': row[c.key] = iso(i - 1); break;
        case 'pill':     row[c.key] = ['Pending', 'In Review', 'Pending', 'Overdue'][i]; break;
        default:         row[c.key] = `${c.caption} ${i + 1}`; break;
      }
    }
    return row;
  });
}

/** Map a configured column → AppColumn for the live preview grid. */
function toPreviewColumn(c: DashColumn): AppColumn<PreviewRow> {
  const col: AppColumn<PreviewRow> = {
    field: c.key,
    header: c.caption,
    width: c.width,
    align: c.format === 'number' ? 'right' : undefined,
    text: r => {
      const v = r[c.key];
      if (v == null) return '';
      if (c.format === 'date') return fmtLocalDate(v as string) || String(v);
      if (c.format === 'datetime') return fmtLocalDateTime(v as string) || String(v);
      return String(v);
    },
  };
  if (c.format === 'pill') {
    col.render = r => <span className="bl-pill warn">{String(r[c.key] ?? '—')}</span>;
  }
  return col;
}

export default function DashboardDesignerPage() {
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const notify = useNotify();

  const [view, setView] = useState<DashViewId>('missing-coc');
  const [def, setDef] = useState<DashDefinition>(() => loadDefinition('missing-coc'));
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  // Switch dashboards — load that view's saved layout.
  const switchView = (v: DashViewId) => { setView(v); setDef(loadDefinition(v)); };

  // Fields not yet shown = catalog minus configured columns.
  const shownKeys = useMemo(() => new Set(def.columns.map(c => c.key)), [def.columns]);
  const available = useMemo(
    () => FIELD_CATALOG[view].filter(f => !shownKeys.has(f.key)),
    [view, shownKeys],
  );

  // ── Column mutations ──────────────────────────────────────────────────────
  const addColumn = (key: string) => {
    const f = FIELD_CATALOG[view].find(x => x.key === key);
    if (!f) return;
    setDef(d => ({ ...d, columns: [...d.columns, { key: f.key, caption: f.label, width: f.defaultWidth, format: f.format }] }));
  };
  const removeColumn = (key: string) =>
    setDef(d => ({ ...d, columns: d.columns.filter(c => c.key !== key) }));
  const patchColumn = (key: string, patch: Partial<DashColumn>) =>
    setDef(d => ({ ...d, columns: d.columns.map(c => (c.key === key ? { ...c, ...patch } : c)) }));

  const moveColumn = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    setDef(d => {
      const cols = [...d.columns];
      const m = cols.splice(from, 1)[0];
      if (!m) return d;
      cols.splice(to, 0, m);
      return { ...d, columns: cols };
    });
  };

  // ── KPI toggles ───────────────────────────────────────────────────────────
  const toggleKpi = (key: string) =>
    setDef(d => ({ ...d, kpis: d.kpis.map(k => (k.key === key ? { ...k, enabled: !k.enabled } : k)) }));

  // ── Save / Reset ──────────────────────────────────────────────────────────
  const handleSave = () => {
    if (def.columns.length === 0) { notify.warning('Add at least one column before saving.'); return; }
    void runSave({
      formName: 'Dashboard Designer',
      auditAction: 'edit',
      confirmMessage: `Save the "${DASH_VIEWS.find(v => v.id === view)?.label}" dashboard layout?`,
      successMessage: 'Dashboard layout saved successfully.',
      save: () => saveDefinition(view, def),
    });
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: 'Reset layout',
      message: 'Reset this dashboard to its default columns and KPIs? Unsaved changes are lost.',
      tone: 'danger',
    });
    if (ok) setDef(defaultDefinition(view));
  };

  const previewCols = useMemo(() => def.columns.map(toPreviewColumn), [def.columns]);
  const previewData = useMemo(() => sampleRows(def), [def]);
  const enabledKpis = def.kpis.filter(k => k.enabled);

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the two
  // toolbar actions, mirroring each button's handler.  Ctrl+S = Save · Alt+C =
  // Reset.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Dashboard Designer', run: () => handleSave() },
    { combo: 'alt+c', label: 'Reset', group: 'Dashboard Designer', run: () => void handleReset() },
  ]);

  return (
    <div className="bl-page">
      <div className="bl-grid-area">
        {/* Header + toolbar */}
        <div className="section-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <FormTitle pageId="dashdesigner" title="Dashboard Designer" />
          <AppToolbar
            left={
              <>
                <span className="dd-toolbar-view">
                  Dashboard:
                  <select value={view} onChange={e => switchView(e.target.value as DashViewId)}>
                    {DASH_VIEWS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </span>
                <ToolbarDivider />
                <button className="ca-btn" onClick={handleSave} title={`Save layout (${formatCombo('mod+s')})`}>
                  <span className="tb-i tb-i-orange">💾</span> Save
                </button>
                <button className="ca-btn" onClick={handleReset} title={`Reset to default (${formatCombo('alt+c')})`}>
                  <span className="tb-i tb-i-grey">↺</span> Reset
                </button>
              </>
            }
          />
        </div>

        <div className="dp-subtitle" style={{ marginBottom: 12 }}>
          Customise columns and KPI cards for this dashboard. Changes apply to every user in your division — no software update needed.
        </div>

        {/* Two config panes */}
        <div className="dd-body">
          {/* Available fields */}
          <div className="dd-pane">
            <div className="dd-pane-hdr">
              Available fields <span className="dd-count">{available.length} field{available.length === 1 ? '' : 's'}</span>
            </div>
            <div className="dd-pane-body">
              {available.length === 0
                ? <div className="dd-avail-empty">All catalog fields are in use.</div>
                : available.map(f => (
                  <div key={f.key} className="dd-avail-item" onClick={() => addColumn(f.key)} title={`Add "${f.label}"`}>
                    <span>{f.label}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span className="dd-fmt-tag">{f.format}</span>
                      <span className="dd-add">＋</span>
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Shown columns */}
          <div className="dd-pane">
            <div className="dd-pane-hdr">
              Shown columns <span className="dd-count">{def.columns.length} · drag to reorder</span>
            </div>
            <div className="dd-pane-body">
              {def.columns.length === 0
                ? <div className="dd-shown-empty">No columns yet — add one from the left.</div>
                : (
                  <>
                    <div className="dd-col-head">
                      <span />
                      <span>Caption</span>
                      <span>Width</span>
                      <span>Format</span>
                      <span />
                    </div>
                    {def.columns.map((c, i) => (
                      <div
                        key={c.key}
                        className={`dd-col-row${dragIdx === i ? ' dd-dragging' : ''}${dropIdx === i ? ' dd-drop-target' : ''}`}
                        draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={e => { e.preventDefault(); setDropIdx(i); }}
                        onDragEnd={() => { if (dragIdx != null && dropIdx != null) moveColumn(dragIdx, dropIdx); setDragIdx(null); setDropIdx(null); }}
                        onDrop={e => { e.preventDefault(); if (dragIdx != null) moveColumn(dragIdx, i); setDragIdx(null); setDropIdx(null); }}
                      >
                        <span className="dd-grip" title="Drag to reorder">⠿</span>
                        <input
                          className="dd-cap"
                          value={c.caption}
                          onChange={e => patchColumn(c.key, { caption: e.target.value })}
                        />
                        <input
                          className="dd-w"
                          type="number" min={40} max={600}
                          value={c.width}
                          onChange={e => patchColumn(c.key, { width: Number(e.target.value) || 40 })}
                        />
                        <select
                          className="dd-fmt"
                          value={c.format}
                          onChange={e => patchColumn(c.key, { format: e.target.value as FieldFormat })}
                        >
                          {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <button className="dd-remove" title="Remove column" onClick={() => removeColumn(c.key)}>✕</button>
                      </div>
                    ))}
                  </>
                )}
            </div>
          </div>
        </div>

        {/* KPI toggles */}
        <div className="dd-section-gap" />
        <div className="dd-pane">
          <div className="dd-pane-hdr">
            KPI cards <span className="dd-count">{enabledKpis.length} of {def.kpis.length} shown</span>
          </div>
          <div className="dd-kpi-list">
            {def.kpis.map(k => (
              <label key={k.key} className={`dd-kpi-chip${k.enabled ? ' on' : ''}`}>
                <input type="checkbox" checked={k.enabled} onChange={() => toggleKpi(k.key)} />
                {k.label}
              </label>
            ))}
          </div>
        </div>

        {/* Live preview */}
        <div className="dd-section-gap" />
        <div className="dd-preview-hdr">
          <i className="ti ti-eye" /> Live preview
          <span className="dd-preview-note">— sample data, exactly how analysts will see this dashboard</span>
        </div>

        {enabledKpis.length > 0 && (
          <div className="kpis-on">
            <div className="kpi-row" style={{ margin: '0 0 12px' }}>
              {enabledKpis.map((k, i) => (
                <div className="kpi" key={k.key}>
                  <div className="kpi-lbl">{k.label.toUpperCase()}</div>
                  <div className={`kpi-val${i === 0 ? ' kpi-orange' : ''}`}>—</div>
                  <div className="kpi-chg">Preview</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bl-card bl-card-flex">
          <AppDataGrid<PreviewRow>
            rows={previewData}
            columns={previewCols}
            getRowId={(_r, i) => i}
            emptyText="Add a column to preview this dashboard"
          />
        </div>
        <div className="dd-section-gap" />
      </div>
    </div>
  );
}
